import React from "react";
import { Box, Typography, Container } from "@mui/material";
import { APP_SCREENSHOTS } from "../../seo/assets";

const frameSx = {
    width: { xs: 160, sm: 180, md: 200 },
    flexShrink: 0,
    borderRadius: "28px",
    overflow: "hidden",
    border: "3px solid #1a1a1a",
    boxShadow:
        "0 20px 50px rgba(46,139,87,0.18), 0 0 0 1px rgba(255,255,255,0.4) inset",
    background: "#0f0f0f",
    position: "relative",
    transition: "transform 0.35s ease, box-shadow 0.35s ease",
    "&:hover": {
        transform: "translateY(-10px)",
        boxShadow: "0 28px 60px rgba(46,139,87,0.28)",
    },
    "&::before": {
        content: '""',
        position: "absolute",
        top: 8,
        left: "50%",
        transform: "translateX(-50%)",
        width: 56,
        height: 6,
        borderRadius: 99,
        bgcolor: "#2a2a2a",
        zIndex: 1,
    },
};

/**
 * Horizontal gallery of App Store screenshots in phone frames.
 */
const ScreenshotGallery = ({
    title = "See Quran Chat Buddy in action",
    subtitle = "AI Quran chat, prayer times, daily ayat, and Islamic learning — designed for Muslims who want to grow closer to Allah.",
    limit = 7,
    showCaptions = true,
}) => {
    const shots = APP_SCREENSHOTS.slice(0, limit);

    return (
        <Box
            sx={{
                py: { xs: 6, md: 10 },
                background:
                    "linear-gradient(180deg, #ffffff 0%, rgba(240,248,240,0.7) 100%)",
            }}
        >
            <Container maxWidth="lg">
                {(title || subtitle) && (
                    <Box sx={{ textAlign: "center", mb: 5 }}>
                        {title && (
                            <Typography
                                variant="h2"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 700 }}
                            >
                                {title}
                            </Typography>
                        )}
                        {subtitle && (
                            <Typography
                                variant="body1"
                                sx={{
                                    color: "text.secondary",
                                    maxWidth: 700,
                                    mx: "auto",
                                    lineHeight: 1.7,
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                )}

                <Box
                    sx={{
                        display: "flex",
                        gap: { xs: 2.5, md: 3.5 },
                        overflowX: "auto",
                        pb: 3,
                        px: 1,
                        scrollSnapType: "x mandatory",
                        "&::-webkit-scrollbar": { height: 8 },
                        "&::-webkit-scrollbar-thumb": {
                            bgcolor: "rgba(46,139,87,0.35)",
                            borderRadius: 99,
                        },
                    }}
                >
                    {shots.map((shot) => (
                        <Box
                            key={shot.src}
                            sx={{
                                scrollSnapAlign: "center",
                                textAlign: "center",
                            }}
                        >
                            <Box sx={frameSx}>
                                <Box
                                    component="img"
                                    src={shot.src}
                                    alt={shot.alt}
                                    loading="lazy"
                                    sx={{
                                        display: "block",
                                        width: "100%",
                                        height: "auto",
                                    }}
                                />
                            </Box>
                            {showCaptions && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "block",
                                        mt: 1.5,
                                        color: "text.secondary",
                                        maxWidth: 180,
                                        mx: "auto",
                                        fontWeight: 500,
                                    }}
                                >
                                    {shot.caption}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

/** Single phone mockup — used in heroes. */
export const PhoneMockup = ({
    src,
    alt = "Quran Chat Buddy app screenshot",
    maxWidth = 280,
}) => (
    <Box
        sx={{
            ...frameSx,
            width: "100%",
            maxWidth,
            mx: "auto",
            borderRadius: "36px",
            borderWidth: 4,
            "&:hover": {
                transform: "translateY(-6px) rotate(0deg)",
            },
        }}
    >
        <Box
            component="img"
            src={src}
            alt={alt}
            sx={{ display: "block", width: "100%", height: "auto" }}
        />
    </Box>
);

export default ScreenshotGallery;
